import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import SeoHelmet from "@/components/SeoHelmet";
import { ArrowRight, Calendar, Clock, User } from "lucide-react";
import { Button } from "@/components/ui/button";

const blogPosts = [
  {
    title: "How to Tailor Your CV for a Specific Job (2026 Guide)",
    slug: "how-to-tailor-your-cv",
    excerpt: "Learn the exact process to match your CV to any job description and bypass Applicant Tracking Systems (ATS) with ease.",
    date: "May 16, 2026",
    author: "Alvaru Team",
    readingTime: "6 min read",
    image: "/blog-tailoring.jpg",
    category: "Career Advice"
  },
  {
    title: "Best ATS-Friendly CV Templates for 2026",
    slug: "best-ats-friendly-cv-templates",
    excerpt: "Learn which CV formats and templates are most effective at passing Applicant Tracking Systems (ATS) in 2026.",
    date: "May 16, 2026",
    author: "Alvaru Team",
    readingTime: "5 min read",
    image: "/blog-ats-templates.jpg",
    category: "CV Templates"
  },
  {
    title: "What Are ATS Keywords and How to Find Them",
    slug: "what-are-ats-keywords",
    excerpt: "Learn how to identify the most important keywords in a job description and integrate them into your CV to pass the ATS screen.",
    date: "May 16, 2026",
    author: "Alvaru Team",
    readingTime: "7 min read",
    image: "/blog-ats-keywords.jpg",
    category: "Job Search Mastery"
  },
  {
    title: "How to Auto-Apply on LinkedIn (Step by Step)",
    slug: "how-to-auto-apply-on-linkedin",
    excerpt: "Save hours of repetitive work with our complete guide to using the Alvaru extension to auto-apply for jobs on LinkedIn.",
    date: "May 16, 2026",
    author: "Alvaru Team",
    readingTime: "8 min read",
    image: "/blog-linkedin-auto-apply.jpg",
    category: "Automation"
  },
  {
    title: "How to Write a CV That Passes ATS (2026 Guide)",
    slug: "how-to-write-a-cv-that-passes-ats",
    excerpt: "The ultimate 10-rule checklist for writing a CV that clears Applicant Tracking System (ATS) filters and gets you seen by recruiters.",
    date: "May 16, 2026",
    author: "Alvaru Team",
    readingTime: "9 min read",
    image: "/blog-cv-pass-ats.jpg",
    category: "CV Writing"
  },
  {
    title: "7 Common CV Mistakes That Get You Rejected by ATS",
    slug: "7-common-cv-mistakes",
    excerpt: "Learn about the seven most frequent CV errors that trigger automatic Applicant Tracking System (ATS) rejection and how to fix them.",
    date: "May 16, 2026",
    author: "Alvaru Team",
    readingTime: "7 min read",
    image: "/blog-cv-mistakes.png",
    category: "Job Search Strategy"
  },
  {
    title: "How to Tailor Your CV for a Career Change (With Examples)",
    slug: "how-to-tailor-your-cv-for-a-career-change",
    excerpt: "Struggling to switch industries? Learn how to reframe your experience, highlight transferable skills, and bypass ATS filters for your new career path.",
    date: "May 16, 2026",
    author: "Alvaru Team",
    readingTime: "8 min read",
    image: "/blog-career-change.png",
    category: "Career Change"
  }
];

const Blog = () => {
  return (
    <div className="min-h-screen bg-[#FAF9F6] dark:bg-[#0A0F1E] text-slate-900 dark:text-white transition-colors duration-300 font-body">
      <SeoHelmet
        title="CV Tips & Job Search Advice"
        description="Job search tips, CV tailoring guides, and AI automation advice. Learn how to get more interviews."
        canonical="https://www.alvaru.com/blog"
      />
      <Navbar />

      <main className="pt-32 pb-20">
        <div className="container mx-auto px-6">
          <header className="max-w-3xl mx-auto text-center mb-16">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-cyan-500/10 border border-cyan-500/20 text-cyan-600 dark:text-cyan-400 text-[10px] font-bold uppercase tracking-widest mb-6"
            >
              Alvaru Blog
            </motion.div>
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              className="text-4xl md:text-5xl lg:text-6xl font-heading font-bold tracking-tight mb-6"
            >
              Career Insights &amp; <br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-600 to-blue-600 dark:from-cyan-400 dark:to-blue-500">Resume Mastery.</span>
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="text-lg text-slate-600 dark:text-slate-400 max-w-2xl mx-auto mb-6"
            >
              Expert advice on navigating the modern job market, optimizing your applications, and landing your dream role with AI-powered tools.
            </motion.p>
            <p className="text-slate-600 dark:text-slate-400 mb-8 max-w-2xl mx-auto">
              Job search tips, CV tailoring guides, and AI automation advice from the Alvaru team.
              Learn how to get more interviews and land your dream job faster.
            </p>
          </header>

          <div className="max-w-6xl mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {blogPosts.map((post, index) => (
                <motion.article
                  key={post.slug}
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 + 0.3 }}
                  className="group bg-white dark:bg-slate-900/50 rounded-2xl overflow-hidden border border-slate-200 dark:border-slate-800 hover:border-cyan-500/30 transition-all shadow-sm hover:shadow-xl dark:shadow-none"
                >
                  <Link to={`/blog/${post.slug}`} className="block overflow-hidden aspect-video relative">
                    <img
                      src={post.image}
                      alt={post.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                    <div className="absolute top-4 left-4">
                      <span className="px-2 py-1 rounded-md bg-cyan-500 text-white dark:text-black text-[10px] font-bold uppercase tracking-wider">
                        {post.category}
                      </span>
                    </div>
                  </Link>
                  <div className="p-6">
                    <div className="flex items-center gap-4 text-[11px] text-slate-500 dark:text-slate-400 mb-4 font-medium uppercase tracking-widest">
                      <span className="flex items-center gap-1"><Calendar className="w-3 h-3" /> {post.date}</span>
                      <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {post.readingTime}</span>
                    </div>
                    <Link to={`/blog/${post.slug}`}>
                      <h2 className="text-xl font-heading font-bold mb-3 group-hover:text-cyan-600 dark:group-hover:text-cyan-400 transition-colors line-clamp-2">
                        {post.title}
                      </h2>
                    </Link>
                    <p className="text-sm text-slate-600 dark:text-slate-400 mb-6 line-clamp-3 leading-relaxed">
                      {post.excerpt}
                    </p>
                    <div className="flex items-center justify-between mt-auto pt-6 border-t border-slate-100 dark:border-slate-800/50">
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 rounded-full bg-cyan-500/10 flex items-center justify-center text-cyan-600 dark:text-cyan-400">
                          <User className="w-4 h-4" />
                        </div>
                        <span className="text-xs font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wider">{post.author}</span>
                      </div>
                      <Link to={`/blog/${post.slug}`}>
                        <Button variant="ghost" size="sm" className="group/btn text-cyan-600 dark:text-cyan-400 font-bold uppercase tracking-widest text-[10px]">
                          Read More
                          <ArrowRight className="w-3.5 h-3.5 ml-2 group-hover/btn:translate-x-1 transition-transform" />
                        </Button>
                      </Link>
                    </div>
                  </div>
                </motion.article>
              ))}
            </div>
          </div>
        </div>
      </main>

      <footer className="py-20 border-t border-slate-200 dark:border-slate-900 bg-white dark:bg-[#060912]">
        <div className="container mx-auto px-6 text-center">
          <h2 className="text-2xl md:text-3xl font-heading font-bold mb-6">Ready to land your dream job?</h2>
          <p className="text-slate-600 dark:text-slate-400 mb-8 max-w-xl mx-auto">Join thousands of professionals using Alvaru to tailor their CVs and get more interviews.</p>
          <Link to="/app">
            <Button size="lg" className="bg-cyan-500 hover:bg-cyan-600 text-white dark:text-black font-bold rounded-xl px-8 py-6 h-auto">
              Start Free Today
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </Link>
        </div>
      </footer>
    </div>
  );
};

export default Blog;
